import React, { useEffect, useRef, useState } from 'react';
import { AuthenticateWithRedirectCallback, TaskResetPassword, useAuth as useClerkAuth, useSession } from '@clerk/react';
import { bridgeClerkSession } from '../api/clerkAuthApi';
import { useAuth } from './AuthContext';
import ClerkAuthenticationScreen from '../components/auth/ClerkAuthenticationScreen';
import ClerkBetaAdmissionScreen from './ClerkBetaAdmissionScreen';
import ClerkRecoveryScreen from './ClerkRecoveryScreen';
import { resolveRecoveryState } from './clerkRecoveryStates';
import { clearClerkEntrySignal } from './clerkEntryPersistence';
import { clearStoredInvite } from './inviteKey';
import { readAdmissionToken, clearAdmissionToken } from './betaAdmissionToken';
import ClerkLoopDiagnosticScreen from './ClerkLoopDiagnosticScreen';
import { FULL_SIGN_OUT_STATES, getFullSignOutState, isFullSignOutBlocked, clearCompletedFullSignOutAfterClerkIsSignedOut } from './fullSignOutState';

/**
 * Is the browser sitting on Clerk's OAuth return route?
 *
 * A social sign-in (Google et al.) leaves the app entirely, and Clerk sends the
 * user back to `#/sso-callback` carrying the handshake parameters. Something must
 * mount `<AuthenticateWithRedirectCallback />` on that route to finish the
 * exchange and actually establish the session.
 *
 * WITHOUT THIS the returning user hit `SIGNED_OUT` — Clerk had not completed the
 * handshake, so `isSignedIn` stayed false — and got dumped back on the login
 * splash with `#/sso-callback` still in the address bar. No bridge request was
 * ever attempted, which is exactly what the backend logs showed: a successful
 * Google sign-in followed by silence.
 *
 * The hash (not the pathname) is checked because Clerk's components run with
 * `routing="virtual"` here, which is hash-based. `startsWith` rather than equality
 * so appended handshake params still match.
 */
export function isClerkRedirectCallback(hash = window.location.hash) {
    if (typeof hash !== 'string') return false;
    const withoutLeadingHash = hash.startsWith('#') ? hash.slice(1) : hash;
    const path = withoutLeadingHash.split('?')[0];
    return path === '/sso-callback' || path.startsWith('/sso-callback/');
}

const BRIDGE_ATTEMPTS_KEY = 'mythic_clerk_bridge_attempts';
const BRIDGE_FAILURES_KEY = 'mythic_clerk_bridge_failures';
const MAX_BRIDGE_FAILURE_RETRIES = 2;
const bridgeAttemptedSessions = new Set();
const ACTIVE_BRIDGE_SESSION_KEY = `${BRIDGE_ATTEMPTS_KEY}:active`;

export function resetBridgeAttemptRegistry() {
    bridgeAttemptedSessions.clear();
}

// A Clerk session id is the durable dedupe identity when available. During the
// brief session-hydration window where Clerk has not exposed that id, the
// transient entry/rollout key is retained and migrated to the real id once it
// appears. This prevents remounts from duplicating one bridge while allowing a
// later, genuinely different session to replace stale markers.
function bridgeSessionKey(session, clerkEntry, publicClerk) {
    return session?.id || `transient:${clerkEntry ? 'entry' : 'public'}:${publicClerk ? 'public' : 'internal'}`;
}

function readActiveBridgeSession() {
    const raw = window.sessionStorage.getItem(ACTIVE_BRIDGE_SESSION_KEY);
    if (!raw) return null;
    try {
        const value = JSON.parse(raw);
        if (!value || (value.sessionId !== null && typeof value.sessionId !== 'string') || typeof value.fallbackKey !== 'string') {
            throw new Error('expected sessionId and fallbackKey');
        }
        return value;
    } catch (error) {
        throw new Error(`Invalid Clerk bridge active-session marker: ${error.message}`);
    }
}

function prepareBridgeSession(session, clerkEntry, publicClerk) {
    const fallbackKey = bridgeSessionKey(null, clerkEntry, publicClerk);
    const sessionId = session?.id || null;
    const active = readActiveBridgeSession();
    if (sessionId && active && active.sessionId && active.sessionId !== sessionId) {
        clearBridgeAttempt(active.sessionId);
        clearBridgeFailure(active.sessionId);
        window.sessionStorage.removeItem(ACTIVE_BRIDGE_SESSION_KEY);
    }
    const current = readActiveBridgeSession();
    if (sessionId && current?.sessionId === null && current.fallbackKey === fallbackKey && hasBridgeBeenAttempted(fallbackKey)) {
        markBridgeAttempted(sessionId);
        const failureKey = `${BRIDGE_FAILURES_KEY}:${fallbackKey}`;
        const failureCount = window.sessionStorage.getItem(failureKey);
        if (failureCount !== null) window.sessionStorage.setItem(`${BRIDGE_FAILURES_KEY}:${sessionId}`, failureCount);
        clearBridgeAttempt(fallbackKey);
        clearBridgeFailure(fallbackKey);
    }
    if (!current || current.sessionId !== sessionId) {
        window.sessionStorage.setItem(ACTIVE_BRIDGE_SESSION_KEY, JSON.stringify({ sessionId, fallbackKey }));
    }
    return sessionId || fallbackKey;
}

function hasBridgeBeenAttempted(key) {
    if (bridgeAttemptedSessions.has(key)) return true;
    return window.sessionStorage.getItem(`${BRIDGE_ATTEMPTS_KEY}:${key}`) === 'true';
}

function markBridgeAttempted(key) {
    bridgeAttemptedSessions.add(key);
    window.sessionStorage.setItem(`${BRIDGE_ATTEMPTS_KEY}:${key}`, 'true');
}

function clearBridgeAttempt(key) {
    bridgeAttemptedSessions.delete(key);
    window.sessionStorage.removeItem(`${BRIDGE_ATTEMPTS_KEY}:${key}`);
}

function clearActiveBridgeSession(key) {
    const active = readActiveBridgeSession();
    if (active && (active.sessionId === key || active.fallbackKey === key)) {
        window.sessionStorage.removeItem(ACTIVE_BRIDGE_SESSION_KEY);
    }
}

function clearBridgeFailure(key) {
    window.sessionStorage.removeItem(`${BRIDGE_FAILURES_KEY}:${key}`);
}

function recordBridgeFailure(key) {
    const storageKey = `${BRIDGE_FAILURES_KEY}:${key}`;
    const previous = Number.parseInt(window.sessionStorage.getItem(storageKey) || '0', 10);
    if (!Number.isInteger(previous) || previous < 0) {
        throw new Error(`Invalid Clerk bridge failure count for session ${key}.`);
    }
    const next = previous + 1;
    window.sessionStorage.setItem(storageKey, String(next));
    if (next <= MAX_BRIDGE_FAILURE_RETRIES) clearBridgeAttempt(key);
    return next;
}

export async function bridgeWithTokenRetry({ getToken, bridge, betaKey, admissionToken }) {
    const token = await getToken();
    if (!token) throw new Error('Clerk returned no completed session token.');

    // At most ONE admission credential, and only when there is one to send.
    //
    // - `admissionToken` is the carrier captured at the pre-Clerk gate; it is what
    //   survives an OAuth redirect and it wins when both exist, matching the
    //   backend's precedence.
    // - `betaKey` is the raw key, present on an in-page signup that carried it in
    //   memory, or on an explicit user-driven admission retry.
    //
    // Spread rather than pass `undefined` so a mapped returning user's automatic
    // first attempt still sends a body-less request identical to the pre-F2
    // contract — that probe must not change shape.
    let payload = {};
    if (admissionToken !== undefined) {
        payload = { admissionToken };
    } else if (betaKey !== undefined) {
        payload = { betaKey };
    }

    try {
        return await bridge({ token, ...payload });
    } catch (error) {
        if (error?.errorType !== 'token_invalid') throw error;

        const freshToken = await getToken({ skipCache: true });
        if (!freshToken) throw new Error('Clerk returned no fresh session token.');
        return bridge({ token: freshToken, ...payload });
    }
}

/**
 * `beta_key_required` is not a failure — it is the backend telling us this Clerk
 * identity has no Django user yet and provisioning needs an admission key. It must
 * render the admission prompt, never the generic bridge-failure recovery screen,
 * and never discard the already-completed Clerk session.
 */
export function isBetaAdmissionRequired(error) {
    return error?.errorType === 'beta_key_required';
}

/**
 * Did the ADMISSION TOKEN die, as opposed to the key being bad?
 *
 * `beta_admission_token_invalid` means the carrier was unknown, expired, or
 * already redeemed — a slow OAuth round trip, a tab left open past the 10-minute
 * TTL, a reused browser-back navigation. The user's key itself may be perfectly
 * valid, so this must land on the ordinary admission prompt with NO error banner
 * accusing them of a bad key, and it must never reach the recovery screen
 * (`clerkRecoveryStates.js` lists it as a non-recovery type for exactly this
 * reason). Discard the dead token and let them type the key.
 */
export function isAdmissionTokenRejected(error) {
    return error?.errorType === 'beta_admission_token_invalid';
}

export function hasPendingSessionTask(session) {
    return Boolean(session?.currentTask);
}

function ClerkBridgeCoordinator({ children, clerkEntry, rolloutState, loopGuard, status }) {
    const { isAuthenticated, isLoading: isLocalAuthLoading, setAuthenticatedUser, logout, fullSignOutVersion } = useAuth();
    const { isLoaded, isSignedIn, getToken } = useClerkAuth();
    const { isLoaded: isSessionLoaded, session } = useSession();
    const pendingSessionTask = hasPendingSessionTask(session);
    const [bridgeError, setBridgeError] = useState(null);
    /*
     * A beta key the user already cleared at the pre-Clerk gate
     * (`BetaKeyGate`, via `ClerkAuthenticationScreen`).
     *
     * WHY THIS LIVES HERE and not in the sign-in screen: that screen unmounts the
     * moment Clerk signs in, so a key held in its state would be gone exactly when
     * the bridge needs it — which is why users were asked for the same key twice.
     * This coordinator stays mounted across SIGNED_OUT -> BRIDGING, so the key
     * survives the transition and the first bridge attempt can carry it.
     *
     * IN-MEMORY ONLY. It is deliberately never written to browser storage: the
     * storage rules allow only a URL-derived SHARED invite in sessionStorage (owned
     * by `inviteKey.js`), never a hand-typed key. The ADMISSION TOKEN is what may be
     * persisted (see `betaAdmissionToken.js`) precisely because it is not the key.
     *
     * WHY THIS IS RETAINED NOW THAT THE TOKEN EXISTS — it is not redundant. The
     * token is the primary carrier and takes precedence, but it can legitimately be
     * absent on the in-page path: `sessionStorage` can be unavailable or full,
     * capture can fail without aborting the signup (`BetaKeyGate` reports and
     * continues by design), and a backend that has not yet shipped the additive
     * `admission_token` field returns none. In every one of those cases the
     * email/password flow still resolves in a single prompt because this in-memory
     * key is here. It costs one state field and removes a whole class of silent
     * regression, so it stays.
     *
     * A redirect-based Clerk flow (Google/OAuth) destroys this state by design —
     * that is exactly the gap the admission token closes. `ClerkBetaAdmissionScreen`
     * remains the authoritative admission point behind both.
     */
    const [pendingBetaKey, setPendingBetaKey] = useState(null);
    // Beta admission is tracked separately from bridgeError so a `beta_key_required`
    // response never renders the generic bridge-failure recovery screen.
    const [betaAdmissionRequired, setBetaAdmissionRequired] = useState(false);
    const [betaAdmissionErrorType, setBetaAdmissionErrorType] = useState(null);
    const [betaAdmissionSubmitting, setBetaAdmissionSubmitting] = useState(false);
    const [fullSignOutBlocked, setFullSignOutBlocked] = useState(() => isFullSignOutBlocked());
    const [fullSignOutRetryError, setFullSignOutRetryError] = useState(null);
    const bridgeInFlight = useRef(false);
    const bridgeAttemptedKey = useRef(null);
    const publicClerk = rolloutState === 'BETA' || rolloutState === 'ACTIVE';

    const retryFullSignOut = async () => {
        // Do not create an unguarded window before logout() starts. logout()
        // immediately replaces the existing recovery marker with in-progress.
        setFullSignOutRetryError(null);
        try {
            await logout();
            if (!isSignedIn && getFullSignOutState() === FULL_SIGN_OUT_STATES.COMPLETED) {
                clearCompletedFullSignOutAfterClerkIsSignedOut();
                setFullSignOutBlocked(false);
            }
        } catch (error) {
            setFullSignOutBlocked(true);
            setFullSignOutRetryError(error);
        }
    };

    const retryBridge = () => {
        if (!bridgeAttemptedKey.current) {
            throw new Error('Cannot retry Clerk bridge before an attempt key exists.');
        }
        clearBridgeAttempt(bridgeAttemptedKey.current);
        clearBridgeFailure(bridgeAttemptedKey.current);
        bridgeAttemptedKey.current = null;
        // A failed attempt can render the recovery screen before the async
        // attempt's finally block runs. Explicit retry owns the next attempt,
        // so release the in-flight guard before clearing the error state.
        bridgeInFlight.current = false;
        setBridgeError(null);
    };

    /**
     * Explicit, user-driven beta admission retry.
     *
     * The single-flight registry deliberately blocks AUTOMATIC duplicate bridge
     * attempts for one session. This retry is not automatic — the user typed a key
     * and pressed a button — so it clears the attempt marker for the current session
     * and issues exactly one provisioning request. The backend consumes the key
     * atomically under lock, so the frontend must not add its own retry/dedup logic
     * that could imply a second concurrent attempt is safe to fire.
     */
    const submitBetaAdmission = async (betaKey) => {
        if (typeof betaKey !== 'string' || !betaKey.trim()) {
            throw new Error('Beta admission requires a non-empty key from the admission screen.');
        }
        if (betaAdmissionSubmitting || bridgeInFlight.current) return;

        const sessionKey = bridgeAttemptedKey.current;
        if (!sessionKey) {
            throw new Error('Cannot submit beta admission before a bridge attempt key exists.');
        }

        setBetaAdmissionSubmitting(true);
        setBetaAdmissionErrorType(null);
        bridgeInFlight.current = true;
        try {
            const response = await bridgeWithTokenRetry({
                getToken,
                bridge: bridgeClerkSession,
                // RAW KEY ONLY on this path, deliberately. The user is on the
                // admission screen because no usable token existed (or one was
                // already rejected), and they have just typed a key. Attaching a
                // token here would take precedence over what they typed and, if it
                // were dead, hard-fail their perfectly good key.
                betaKey
            });
            clearClerkEntrySignal();
            // The key was consumed (or the identity resolved). Drop the stored invite
            // so it cannot prefill a later, unrelated signup in this same tab.
            clearStoredInvite();
            // Admission is settled for this identity; nothing may still be holding a
            // spendable carrier.
            clearAdmissionToken();
            clearBridgeFailure(sessionKey);
            clearBridgeAttempt(sessionKey);
            clearActiveBridgeSession(sessionKey);
            setBetaAdmissionRequired(false);
            setAuthenticatedUser(response.user);
        } catch (error) {
            if (isAdmissionTokenRejected(error)) {
                // Defensive: this path sends no token, so the backend should not be
                // able to answer with one being invalid. If it does, a stale carrier
                // is loose somewhere — drop it and keep the user on the prompt rather
                // than escalating to a recovery screen over a credential they never
                // submitted.
                clearAdmissionToken();
                setBetaAdmissionErrorType(null);
            } else if (isBetaAdmissionRequired(error) || error?.errorType === 'beta_key_invalid') {
                // Stay on the admission screen with a non-enumerating message.
                // The Clerk session is untouched and the user may try another key.
                setBetaAdmissionErrorType('beta_key_invalid');
            } else {
                // Anything else is a real bridge failure and belongs on the
                // recovery screen, not the admission prompt. The token cannot be
                // reused after a failed attempt, so it must not survive this either.
                clearAdmissionToken();
                setBetaAdmissionRequired(false);
                setBridgeError(error);
            }
        } finally {
            bridgeInFlight.current = false;
            setBetaAdmissionSubmitting(false);
        }
    };

    useEffect(() => {
        if (!isSignedIn) {
            resetBridgeAttemptRegistry();
            // Clerk is signed out, so any pending admission belongs to an identity
            // that no longer exists in this tab. Leaving it set would drop the next
            // signed-in user straight back onto the admission prompt.
            setBetaAdmissionRequired(false);
            setBetaAdmissionErrorType(null);
            /*
             * DO NOT clear the admission token here. `!isSignedIn` is exactly the
             * state a returning OAuth user is in — Clerk has not finished the
             * handshake yet, so this branch runs on the `#/sso-callback` leg of a
             * signup that is still in progress. Clearing here would destroy the
             * token moments before the bridge needs it and reinstate the second
             * prompt this whole mechanism exists to remove.
             *
             * Real sign-out is handled by `AuthProvider.logout()`, which clears it
             * explicitly along with the rest of the session's storage.
             */
            const signOutState = getFullSignOutState();
            if (signOutState === FULL_SIGN_OUT_STATES.COMPLETED) {
                clearCompletedFullSignOutAfterClerkIsSignedOut();
                setFullSignOutBlocked(false);
            } else if (signOutState === FULL_SIGN_OUT_STATES.FAILED || signOutState === FULL_SIGN_OUT_STATES.IN_PROGRESS) {
                setFullSignOutBlocked(true);
            } else {
                setFullSignOutBlocked(false);
            }
        } else if (isFullSignOutBlocked()) {
            setFullSignOutBlocked(true);
        }
        if (loopGuard?.blocked || isFullSignOutBlocked() || fullSignOutBlocked || !isLoaded || !isSessionLoaded || !isSignedIn || pendingSessionTask || isAuthenticated || isLocalAuthLoading === true || bridgeError || betaAdmissionRequired || bridgeInFlight.current) {
            return undefined;
        }
        const sessionKey = prepareBridgeSession(session, clerkEntry, publicClerk);
        if (bridgeAttemptedKey.current === sessionKey || hasBridgeBeenAttempted(sessionKey)) {
            bridgeAttemptedKey.current = sessionKey;
            return undefined;
        }
        bridgeAttemptedKey.current = sessionKey;
        markBridgeAttempted(sessionKey);
        bridgeInFlight.current = true;
        let active = true;
        (async () => {
            /*
             * WHICH ADMISSION CREDENTIAL RIDES ALONG, IF ANY.
             *
             * Read the token HERE, at attempt time, rather than at render: on the
             * OAuth return leg this component mounts fresh in a brand-new document,
             * so storage is the only place the earlier precheck's result can still
             * exist. `readAdmissionToken()` returns null for a missing, corrupt, or
             * already-expired record and self-clears it, so an expired token simply
             * becomes "no credential" and the backend answers `beta_key_required` —
             * the ordinary admission prompt, not a dead end and not a loop.
             *
             * Precedence matches the backend: token first, then the in-memory key
             * from an in-page (email/password) signup.
             *
             * A mapped RETURNING user is resolved by the backend before admission is
             * considered, so neither credential is spent on someone who already has
             * an account. And when there is neither, the object below is empty and
             * the request stays body-less — byte-identical to the original probe.
             */
            const storedAdmissionToken = readAdmissionToken();
            let admissionPayload = {};
            if (storedAdmissionToken) {
                admissionPayload = { admissionToken: storedAdmissionToken };
            } else if (pendingBetaKey) {
                admissionPayload = { betaKey: pendingBetaKey };
            }

            try {
                const response = await bridgeWithTokenRetry({
                    getToken,
                    bridge: bridgeClerkSession,
                    ...admissionPayload
                });
                clearClerkEntrySignal();
                clearBridgeFailure(sessionKey);
                // Consumed (or unnecessary) — do not carry either into a later
                // attempt. The token is single-use and the backend has already
                // burned it; leaving it in storage could only produce a confusing
                // `beta_admission_token_invalid` for an unrelated signup in this tab.
                setPendingBetaKey(null);
                clearAdmissionToken();
                // Keep the transient attempt marker until Clerk exposes its
                // session id. This lets a remount migrate the completed attempt
                // from the null-session fallback key instead of bridging twice.
                if (session?.id) {
                    clearBridgeAttempt(sessionKey);
                    clearActiveBridgeSession(sessionKey);
                }
                if (active) {
                    setAuthenticatedUser(response.user);
                }
            } catch (error) {
                // `beta_key_required` is an expected provisioning step, not a bridge
                // failure: do not count it against the retry cap, do not surface the
                // generic recovery screen, and do not disturb the Clerk session.
                //
                // `beta_key_invalid` is handled the same way when we supplied a
                // credential: the key passed the read-only pre-check but the
                // authoritative check under lock refused it (e.g. it was exhausted by
                // someone else in between, or revoked mid-redirect while a valid
                // token pointed at it). That is an admission outcome, not a transport
                // failure, so it belongs on the admission prompt where the user can
                // try another key — never on the generic recovery screen.
                //
                // `beta_admission_token_invalid` is DIFFERENT and must not be folded
                // into the above: the CARRIER died (unknown, expired, or already
                // redeemed), which says nothing about the user's key. Show the plain
                // admission prompt with NO error banner — telling someone holding a
                // valid invite that their key is bad would send them off to request a
                // replacement they do not need.
                const suppliedCredential = Boolean(storedAdmissionToken) || Boolean(pendingBetaKey);
                const admissionRejectedSuppliedKey = (
                    error?.errorType === 'beta_key_invalid' && suppliedCredential
                );
                const admissionTokenRejected = isAdmissionTokenRejected(error);
                if (isBetaAdmissionRequired(error) || admissionRejectedSuppliedKey || admissionTokenRejected) {
                    // Drop both credentials so a retry cannot silently resend a
                    // rejected key or a spent/dead token. Done outside the `active`
                    // guard because storage outlives this component instance — an
                    // unmounted attempt must still not leave a dead token behind.
                    clearAdmissionToken();
                    if (active) {
                        setPendingBetaKey(null);
                        setBetaAdmissionRequired(true);
                        setBetaAdmissionErrorType(
                            admissionRejectedSuppliedKey ? 'beta_key_invalid' : null
                        );
                    }
                    return;
                }
                // A genuine bridge failure. The token cannot survive it: it is
                // single-use and may or may not have been burned server-side, so
                // reusing it on the retry is never safe. The user falls back to the
                // admission prompt if provisioning is still owed after recovery.
                clearAdmissionToken();
                const failureCount = recordBridgeFailure(sessionKey);
                if (active) setBridgeError(error);
                if (failureCount > MAX_BRIDGE_FAILURE_RETRIES) {
                    console.error('Clerk bridge retry cap reached', { sessionKey, failureCount });
                }
            } finally {
                bridgeInFlight.current = false;
            }
        })();
        return () => { active = false; };
    // `pendingBetaKey` is intentionally NOT a dependency. It is read inside the
    // async attempt, and the single-flight registry means the effect fires once per
    // Clerk session — adding it would let clearing the key on success re-run the
    // effect for an attempt that already completed. It is always set BEFORE Clerk
    // signs in (at the pre-Clerk gate), so the value is in place by the first run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bridgeError, betaAdmissionRequired, clerkEntry, fullSignOutBlocked, fullSignOutVersion, getToken, isAuthenticated, isLoaded, isLocalAuthLoading, isSessionLoaded, isSignedIn, pendingSessionTask, publicClerk, session, setAuthenticatedUser, loopGuard]);

    if (loopGuard?.blocked) {
        return (
            <ClerkLoopDiagnosticScreen
                status={status}
                loopGuard={loopGuard}
                session={session}
                isSignedIn={isSignedIn}
                isAuthenticated={isAuthenticated}
            />
        );
    }
    // Full-sign-out recovery outranks beta admission. The admission screen offers a
    // sign-out affordance, so if that sign-out partially fails the user must see the
    // recovery alert rather than being silently returned to the admission prompt with
    // a half-cleared session.
    if (isFullSignOutBlocked() || fullSignOutBlocked) {
        return (
            <div role="alert">
                <p>Full sign-out could not be completed. Your application session was cleared.</p>
                <button type="button" onClick={retryFullSignOut}>Try sign-out again</button>
                {fullSignOutRetryError && <p role="status">Retry failed: {fullSignOutRetryError.message}</p>}
            </div>
        );
    }
    // Beta admission is checked BEFORE the generic bridge-error branch. A
    // `beta_key_required` response is a provisioning step for a valid Clerk
    // session, not a failure, and must never render the recovery screen.
    if (betaAdmissionRequired) {
        return (
            <ClerkBetaAdmissionScreen
                onSubmit={submitBetaAdmission}
                isSubmitting={betaAdmissionSubmitting}
                errorType={betaAdmissionErrorType}
                onSignOut={logout}
            />
        );
    }
    if (bridgeError) {
        // Slice F3: every failure resolves to an explicit, itemized recovery state.
        // resolveRecoveryState() throws on an unmapped error_type rather than
        // degrading to a vague message, so a new backend code surfaces loudly in
        // development instead of shipping a dead-end screen.
        return (
            <ClerkRecoveryScreen
                recovery={resolveRecoveryState(bridgeError)}
                onRetry={retryBridge}
                onSignOut={logout}
            />
        );
    }
    const coordinatorState = deriveCoordinatorState({
        isLoaded,
        isSessionLoaded,
        isLocalAuthLoading,
        isAuthenticated,
        pendingSessionTask,
        isSignedIn,
        clerkEntry,
        publicClerk
    });
    if (coordinatorState === 'LOADING') {
        return <div role="status">Loading authentication…</div>;
    }
    if (coordinatorState === 'AUTHENTICATED' || coordinatorState === 'LEGACY') return children;
    if (coordinatorState === 'TASK_PENDING') {
        return (
            <main aria-label="Reset password session task">
                <TaskResetPassword redirectUrlComplete="/" />
            </main>
        );
    }
    if (coordinatorState === 'SIGNED_OUT') {
        /*
         * OAUTH RETURN LEG — must be handled BEFORE rendering the sign-in splash.
         *
         * A social sign-in leaves the app; Clerk returns to `#/sso-callback` with
         * the handshake params. At that instant Clerk has NOT completed the
         * exchange, so `isSignedIn` is still false and we land in SIGNED_OUT.
         * Rendering the splash here was the bug: the user had just authenticated
         * with Google, but was shown Sign In / Sign Up again on a
         * `#/sso-callback` URL, and no bridge request was ever made.
         *
         * `<AuthenticateWithRedirectCallback />` consumes those params and finishes
         * the session. Once it does, `isSignedIn` flips true, this component
         * re-renders into BRIDGING, and the normal bridge effect runs — so the
         * user proceeds into the app instead of back to the login screen.
         */
        if (isClerkRedirectCallback()) {
            return (
                <main aria-label="Completing sign-in">
                    <div role="status">Completing sign-in…</div>
                    {/*
                      * `transferable={false}` is REQUIRED here, not just on <SignIn>.
                      *
                      * `HandleOAuthCallbackParams` also accepts TransferableOption,
                      * and it too defaults to `true`. The OAuth transfer decision is
                      * finalized on this return leg, so setting the prop only on
                      * <SignIn> would leave the callback free to convert an unknown
                      * Google identity into a fresh Clerk signup — bypassing
                      * `BetaKeyGate` at the last possible moment.
                      *
                      * Both call sites must refuse the transfer for the beta gate to
                      * actually hold.
                      */}
                    <AuthenticateWithRedirectCallback transferable={false} />
                </main>
            );
        }

        /*
         * `onBetaKeyValidated` lets the pre-Clerk gate hand its validated key up to
         * this coordinator, which outlives the sign-in screen. The first bridge
         * attempt then carries it, so an in-page signup is not asked for the same
         * key twice. `ClerkBetaAdmissionScreen` still handles every case where the
         * key cannot survive (OAuth redirect, page reload) and remains the
         * authoritative admission point.
         */
        return <ClerkAuthenticationScreen onBetaKeyValidated={setPendingBetaKey} />;
    }
    if (coordinatorState === 'BRIDGING') {
        return <div role="status">Completing sign-in…</div>;
    }
    throw new Error(`Unknown Clerk coordinator state: ${coordinatorState}`);
}

export function deriveCoordinatorState({ isLoaded, isSessionLoaded, isLocalAuthLoading, isAuthenticated, pendingSessionTask, isSignedIn, clerkEntry, publicClerk }) {
    if (isLoaded !== true || isSessionLoaded !== true || isLocalAuthLoading === true) return 'LOADING';
    if (isAuthenticated === true) return 'AUTHENTICATED';
    if (pendingSessionTask) return 'TASK_PENDING';
    if (isSignedIn === false && (clerkEntry || publicClerk)) return 'SIGNED_OUT';
    if (isSignedIn === false && !clerkEntry && !publicClerk) return 'LEGACY';
    if (isSignedIn === true && !pendingSessionTask && isAuthenticated === false) return 'BRIDGING';
    throw new Error(`Unexpected Clerk coordinator inputs: isSignedIn=${String(isSignedIn)}, isAuthenticated=${String(isAuthenticated)}`);
}

export default ClerkBridgeCoordinator;
